import { useParams, Navigate } from "@/lib/react-router-dom-shim";
import DOMPurify from "dompurify";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { ResortSearchForm } from "@/components/ResortSearchForm";
import { useState, useMemo, useEffect, useRef, startTransition } from "react";
import ProductCard from "@/components/ProductCard";
import { HotelFiltersSidebar } from "@/components/HotelFiltersSidebar";
import { LazyHotelsMapModal } from "@/components/LazyHotelsMapModal";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { IterpecSearchResponse, IterpecHotel } from "@/types/iterpec";
import { ResortSearchParams, RESORT_DESTINATIONS } from "@/types/resort";
import { RESORT_REGIONS } from "@/data/resortRegions";
import { ResponsiveHeroImage } from "@/components/ResponsiveHeroImage";
import { useCart } from "@/hooks/useCart";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParams, useNavigate } from "@/lib/react-router-dom-shim";
import { Button } from "@/components/ui/button";
import { Map, Search, Palmtree, Lightbulb, RefreshCw, HelpCircle } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { translateBoardType } from "@/utils/boardTypeTranslations";
import { SchemaOrg, getBreadcrumbSchema, getOrganizationSchema, getWebSiteSchema, getResortServiceSchema, getFAQSchema } from "@/components/seo/SchemaOrg";
import { HreflangTags } from "@/components/seo/HreflangTags";
import { useTranslation } from "react-i18next";
import { usePrice } from "@/hooks/usePrice";
import { useLocale } from "@/contexts/LocaleContext";
import { LocaleKey } from "@/i18n";
import { getLocalizedUrl } from "@/hooks/useSeoMeta";
import { useRecommendedHotels } from "@/hooks/useRecommendedHotels";

const ResortRegion = () => {
  const { regionSlug } = useParams<{ regionSlug: string }>();
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();
  const { format: formatPrice, currency } = usePrice();
  const [searchResults, setSearchResults] = useState<IterpecHotel[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchParams, setSearchParams] = useState<ResortSearchParams | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const { toast } = useToast();
  const [searchTimestamp, setSearchTimestamp] = useState<Date | null>(null);
  const [urlSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasAutoSearched = useRef(false);
  const [searchToken, setSearchToken] = useState<string | null>(null);
  const { addItem } = useCart();
  const resultsRef = useRef<HTMLDivElement>(null);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [destinationDisplayName, setDestinationDisplayName] = useState<string>("");
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const MAX_RETRIES = 2;

  // Get region config
  const regionConfig = regionSlug ? RESORT_REGIONS[regionSlug] : undefined;
  const resortDestination = regionSlug ? RESORT_DESTINATIONS.find(d => d.id === regionSlug) : undefined;

  // Redirect if invalid region
  if (!regionConfig || !resortDestination) {
    return <Navigate to="/resorts" replace />;
  }

  const searchTips = useMemo(() => [
    t('resorts.searchTips.tip1'),
    t('resorts.searchTips.tip2'),
    t('resorts.searchTips.tip3'),
    t('resorts.searchTips.tip4'),
    t('resorts.searchTips.tip5'),
  ], [t]);

  useEffect(() => {
    if (!isSearching) {
      setCurrentTipIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % searchTips.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isSearching, searchTips.length]);

  const ITEMS_PER_PAGE = 12;

  const maxPricePerNight = useMemo(() => {
    if (!searchResults || searchResults.length === 0 || !searchParams) return 500;
    const totalNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;
    const pricesPerNight = searchResults.flatMap(h => 
      h.Rooms.map(r => {
        const priceEur = r.PriceEur || 0;
        return totalNights > 0 ? priceEur / totalNights : priceEur;
      }).filter((p): p is number => p > 0)
    );
    return pricesPerNight.length > 0 ? Math.ceil(Math.max(...pricesPerNight)) : 500;
  }, [searchResults, searchParams]);

  const boardTypes = useMemo(() => {
    if (!searchResults) return [];
    const boards = searchResults.flatMap(
      (h) => h.Rooms?.map((r) => r.BoardDescription).filter(Boolean) || []
    );
    const translatedBoards = boards.map((board) => translateBoardType(board, locale));
    return [...new Set(translatedBoards)].sort();
  }, [searchResults, locale]);

  const starRatings = useMemo(() => {
    if (!searchResults) return [];
    const ratings = searchResults.map(h => (h as any).Category || h.StarRating).filter((r): r is number => r > 0);
    return [...new Set(ratings)].sort((a, b) => b - a);
  }, [searchResults]);

  const hotelIds = useMemo(() => {
    return searchResults.map(h => h.HotelId.toString());
  }, [searchResults]);

  const { recommendedHotelIds, hasRecommendedHotels } = useRecommendedHotels(hotelIds);

  const [filters, setFilters] = useState({
    priceRange: [0, 500] as [number, number],
    sortBy: "default",
    boardTypes: [] as string[],
    starRatings: [] as number[],
    cancellationPolicy: "all",
    hotelNameSearch: "",
    recommendedOnly: false,
  });

  const userAdjustedPriceRef = useRef(false);

  useEffect(() => {
    if (!hasSearched) return;
    if (!userAdjustedPriceRef.current) {
      const upper = Math.ceil(maxPricePerNight || 0);
      setFilters((prev) => ({
        ...prev,
        priceRange: [0, upper],
      }));
    }
  }, [maxPricePerNight, hasSearched]);

  const prevParamsRef = useRef<string>("");

  useEffect(() => {
    const checkIn = urlSearchParams.get('checkIn');
    const checkOut = urlSearchParams.get('checkOut');
    const adults = urlSearchParams.get('adults');
    const childrenAges = urlSearchParams.get('childrenAges');

    const currentParamsKey = `${regionSlug}-${checkIn}-${checkOut}-${adults}-${childrenAges}`;

    if (checkIn && checkOut && adults && resortDestination) {
      if (prevParamsRef.current === currentParamsKey && hasAutoSearched.current) {
        return;
      }
      
      hasAutoSearched.current = true;
      prevParamsRef.current = currentParamsKey;
      
      const params: ResortSearchParams = {
        resortDestinationId: resortDestination.id,
        resortDestinationName: t(resortDestination.nameKey) || resortDestination.name,
        hotelIds: resortDestination.hotelIds,
        checkIn,
        checkOut,
        adults: parseInt(adults),
        childrenAges: childrenAges ? childrenAges.split(',').map(Number).filter(age => !isNaN(age)) : [],
      };
      
      handleSearch(params);
    }
  }, [urlSearchParams, regionSlug, resortDestination]);

  const handleFiltersChange = (next: typeof filters) => {
    if (
      next?.priceRange &&
      (next.priceRange[0] !== filters.priceRange[0] ||
        next.priceRange[1] !== filters.priceRange[1])
    ) {
      userAdjustedPriceRef.current = true;
    }
    startTransition(() => {
      setFilters(next);
    });
  };

  const handleSearch = async (params: ResortSearchParams, isRetry = false) => {
    setIsSearching(true);
    setHasSearched(true);
    setSearchParams(params);
    if (!isRetry) {
      setCurrentPage(1);
      userAdjustedPriceRef.current = false;
      setSearchTimestamp(new Date());
      setRetryAttempt(0);
    }
    
    if (params.resortDestinationName) {
      setDestinationDisplayName(params.resortDestinationName);
    }
    
    const currentAttempt = isRetry ? retryAttempt + 1 : 0;
    if (isRetry) {
      setRetryAttempt(currentAttempt);
    }
    
    try {
      const { data, error } = await supabase.functions.invoke('iterpec-search-resorts', {
        body: {
          hotelIds: params.hotelIds,
          checkIn: params.checkIn,
          checkOut: params.checkOut,
          adults: params.adults,
          childrenAges: params.childrenAges,
        }
      });

      if (error) throw error;

      const response = data as IterpecSearchResponse;
      const hotels = response.Hotels || [];
      
      if (hotels.length === 0 && currentAttempt < MAX_RETRIES) {
        toast({
          title: t('resorts.retrying'),
          description: t('resorts.retryingDescription', { attempt: currentAttempt + 1, max: MAX_RETRIES }),
        });
        await new Promise(resolve => setTimeout(resolve, 2000));
        return handleSearch(params, true);
      }
      
      setSearchResults(hotels);
      setSearchToken(response.Token || null);
      setRetryAttempt(0);
      
      setTimeout(() => {
        if (resultsRef.current) {
          const yOffset = -80;
          const element = resultsRef.current;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
      
      if (hotels.length > 0) {
        toast({
          title: t('resorts.searchComplete'),
          description: t('resorts.resortsFound', { count: hotels.length }),
        });
      } else {
        toast({
          title: t('resorts.noResultsTitle'),
          description: t('resorts.noResultsAfterRetries'),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error searching resorts:', error);
      if (currentAttempt < MAX_RETRIES) {
        toast({
          title: t('resorts.retrying'),
          description: t('resorts.retryingDescription', { attempt: currentAttempt + 1, max: MAX_RETRIES }),
        });
        await new Promise(resolve => setTimeout(resolve, 2000));
        return handleSearch(params, true);
      }
      toast({
        title: t('errors.generic'),
        description: t('errors.searchFailed'),
        variant: "destructive",
      });
      setSearchResults([]);
      setRetryAttempt(0);
    } finally {
      setIsSearching(false);
    }
  };

  const filteredAndSortedHotels = useMemo(() => {
    let filtered = searchResults;
    const totalNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;

    if (filters.recommendedOnly) {
      filtered = filtered.filter(hotel => recommendedHotelIds.has(hotel.HotelId.toString()));
    }

    if (filters.hotelNameSearch && filters.hotelNameSearch.trim()) {
      const searchTerm = filters.hotelNameSearch.toLowerCase().trim();
      filtered = filtered.filter(hotel => hotel.Name.toLowerCase().includes(searchTerm));
    }

    filtered = filtered.filter(hotel => {
      if (!hotel.Rooms || hotel.Rooms.length === 0) return true;
      const prices = hotel.Rooms.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0);
      if (prices.length === 0) return true;
      const minRoomPriceTotal = Math.min(...prices);
      const minRoomPricePerNight = totalNights > 0 ? minRoomPriceTotal / totalNights : minRoomPriceTotal;
      return minRoomPricePerNight >= filters.priceRange[0] && minRoomPricePerNight <= filters.priceRange[1];
    });

    if (filters.starRatings.length > 0) {
      filtered = filtered.filter(hotel => {
        const rating = (hotel as any).Category || hotel.StarRating;
        return filters.starRatings.includes(rating);
      });
    }

    if (filters.boardTypes.length > 0) {
      filtered = filtered.filter((hotel) => {
        if (!hotel.Rooms) return false;
        return hotel.Rooms.some((room) => {
          if (!room.BoardDescription) return false;
          const translatedBoard = translateBoardType(room.BoardDescription, locale);
          return filters.boardTypes.includes(translatedBoard);
        });
      });
    }

    filtered.sort((a, b) => {
      const aIsRecommended = recommendedHotelIds.has(a.HotelId.toString());
      const bIsRecommended = recommendedHotelIds.has(b.HotelId.toString());
      if (!filters.recommendedOnly && aIsRecommended !== bIsRecommended) {
        return aIsRecommended ? -1 : 1;
      }
      const aPrices = a.Rooms?.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0) || [];
      const bPrices = b.Rooms?.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0) || [];
      const aPrice = aPrices.length > 0 ? Math.min(...aPrices) : 0;
      const bPrice = bPrices.length > 0 ? Math.min(...bPrices) : 0;
      switch (filters.sortBy) {
        case "price_asc": return aPrice - bPrice;
        case "price_desc": return bPrice - aPrice;
        case "name_asc": return a.Name.localeCompare(b.Name);
        case "name_desc": return b.Name.localeCompare(a.Name);
        default: return 0;
      }
    });

    return filtered;
  }, [searchResults, filters, searchParams, locale, recommendedHotelIds]);

  const paginatedHotels = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredAndSortedHotels.slice(startIndex, endIndex);
  }, [filteredAndSortedHotels, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedHotels.length / ITEMS_PER_PAGE);

  const baseUrl = getLocalizedUrl(locale as LocaleKey, '/');
  const regionName = t(regionConfig.nameKey);
  
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: t('nav.home'), url: baseUrl },
    { name: t('resorts.breadcrumb'), url: getLocalizedUrl(locale as LocaleKey, '/resorts') },
    { name: regionName, url: '' }
  ]);
  const organizationSchema = getOrganizationSchema(locale as LocaleKey);
  const webSiteSchema = getWebSiteSchema(locale as LocaleKey);
  const resortServiceSchema = getResortServiceSchema(locale as LocaleKey, regionName);
  
  // FAQ Schema
  const faqData = regionConfig.faqs.map(faq => ({
    question: t(faq.questionKey),
    answer: t(faq.answerKey)
  }));
  const faqSchema = getFAQSchema(faqData);
  
  const pageTitle = t(`resorts.regions.${regionSlug}.metaTitle`);
  const pageDescription = t(`resorts.regions.${regionSlug}.metaDescription`);
  const canonicalUrl = getLocalizedUrl(locale as LocaleKey, `/resorts/${regionSlug}`);
  const ogImage = regionConfig.ogImage;

  const renderResortCard = (hotel: IterpecHotel) => {
    const prices = hotel.Rooms?.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0) || [];
    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
    const images = hotel.ThumbUrl ? [hotel.ThumbUrl] : [];
    const cheapestRoomIndex = hotel.Rooms?.findIndex(r => r.PriceEur === minPrice) ?? -1;
    const cheapestRoom = cheapestRoomIndex >= 0 ? hotel.Rooms?.[cheapestRoomIndex] : null;
    const totalNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;
    const pricePerNight = totalNights > 0 ? minPrice / totalNights : minPrice;

    const numNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;
    
    const params = new URLSearchParams({
      checkIn: searchParams?.checkIn || '',
      numNights: numNights.toString(),
      adults: searchParams?.adults?.toString() || '',
    });
    
    if (hotel.Destination?.Id) {
      params.append('destinationId', hotel.Destination.Id.toString());
    }
    if (searchParams?.resortDestinationName) {
      params.append('destinationName', searchParams.resortDestinationName);
    }
    if (searchParams?.childrenAges && searchParams.childrenAges.length > 0) {
      params.append('childrenAges', searchParams.childrenAges.join(','));
    }
    params.append('origin', 'resorts');
    if (searchParams?.resortDestinationId) {
      params.append('resortDestinationId', searchParams.resortDestinationId);
    }
    
    const detailUrl = `/hoteles/${hotel.HotelId}?${params.toString()}`;
    const isRecommended = recommendedHotelIds.has(hotel.HotelId.toString());
    
    return (
      <ProductCard 
        key={hotel.HotelId}
        detailsUrl={detailUrl}
        onAddToCart={async () => {}}
        isRecommended={isRecommended}
        product={{
          id: hotel.HotelId.toString(),
          name: hotel.Name,
          name_es: hotel.Name,
          description: hotel.Address,
          description_es: hotel.Address,
          product_type: "hotel",
          price_eur: minPrice,
          location: hotel.Address,
          images: images,
          metadata: {
            starRating: (hotel as any).Category || hotel.StarRating,
            rooms: hotel.Rooms,
            position: hotel.Position,
            roomDescription: cheapestRoom?.RoomDescription,
            boardDescription: cheapestRoom?.BoardDescription,
            pricePerNight,
            totalNights,
          },
          provider: "iterpec",
          is_active: true,
        }} 
      />
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <HreflangTags path={`/resorts/${regionSlug}`} />
<SchemaOrg schema={[organizationSchema, webSiteSchema, breadcrumbSchema, resortServiceSchema, faqSchema]} />
      
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] overflow-hidden">
        <ResponsiveHeroImage imageUrl={regionConfig.heroImage} />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/60 via-foreground/40 to-foreground/70" />
        
        <div className="container relative mx-auto flex min-h-[70vh] flex-col items-center justify-center px-4 py-20 text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 px-4 py-2 backdrop-blur-sm">
            <span className="text-sm font-medium text-primary-foreground">{t('resorts.badge')}</span>
          </div>
          
          <h1 className="mb-4 font-serif text-4xl font-bold leading-tight text-primary-foreground md:text-5xl lg:text-6xl">
            {t(`resorts.regions.${regionSlug}.heroTitle`)}
          </h1>
          
          <p className="mb-10 max-w-2xl text-lg text-primary-foreground/80">
            {t(`resorts.regions.${regionSlug}.heroSubtitle`)}
          </p>

          <div className="w-full max-w-4xl">
            <ResortSearchForm defaultRegion={regionSlug} />
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full" preserveAspectRatio="none">
            <path d="M0 60L1440 60L1440 30C1200 50 960 10 720 30C480 50 240 10 0 30L0 60Z" fill="hsl(var(--background))" />
          </svg>
        </div>
      </section>

      <main className="container mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            { label: t('nav.home'), href: "/" },
            { label: t('resorts.breadcrumb'), href: "/resorts" },
            { label: regionName },
          ]}
        />

        {/* SEO Content Section */}
        <section className="my-12 prose prose-lg max-w-none dark:prose-invert">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">
            {t(`resorts.regions.${regionSlug}.seoTitle`)}
          </h2>
          <div 
            className="text-muted-foreground leading-relaxed [&_h2]:text-xl [&_h2]:font-serif [&_h2]:font-bold [&_h2]:text-foreground [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground [&_h3]:mt-6 [&_h3]:mb-3 [&_p]:mb-4 [&_strong]:text-foreground"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t(`resorts.regions.${regionSlug}.seoContent`)) }}
          />
        </section>

        {/* Featured Destinations Title */}
        <h2 className="text-2xl font-serif font-bold text-foreground mb-6">
          {t('resorts.featuredDestinations')}
        </h2>

        {/* Search Results */}
        <div ref={resultsRef}>
          {isSearching ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-lg font-medium">{t('resorts.searching')}</p>
              <p className="text-sm text-muted-foreground mt-2">{t('resorts.searchingSubtitle')}</p>
              <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                <Lightbulb className="h-4 w-4 text-primary" />
                <span>{searchTips[currentTipIndex]}</span>
              </div>
            </div>
          ) : hasSearched && searchResults.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedHotels.map(renderResortCard)}
            </div>
          ) : hasSearched ? (
            <div className="text-center py-16">
              <Palmtree className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('resorts.noResultsTitle')}</h3>
              <p className="text-muted-foreground">{t('resorts.noResultsDescription')}</p>
            </div>
          ) : (
            <div className="text-center py-16">
              <Search className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('resorts.selectDatesAbove')}</h3>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(page => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => setCurrentPage(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>

        {/* FAQ Section */}
        <section className="my-16">
          <div className="flex items-center gap-3 mb-8">
            <HelpCircle className="h-8 w-8 text-primary" />
            <h2 className="text-2xl font-serif font-bold text-foreground">
              {t('resorts.faq.title')}
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {regionConfig.faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`}
                className="border border-border rounded-lg px-6 bg-card"
              >
                <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline py-5">
                  {t(faq.questionKey)}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                  {t(faq.answerKey)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ResortRegion;
